import { useToast } from './use-toast'
import * as Sentry from '@sentry/nextjs'
import { isAxiosError } from 'axios'
import { FormikErrors } from 'formik'
import { useTranslation } from 'next-i18next'
import { useCallback } from 'react'
import { camelCaseObject } from '@/helper/formatter'

export function useErrors() {
  const { toast } = useToast()
  const { t } = useTranslation()

  const handleError = useCallback(
    (error: any, setErrors?: (errors: FormikErrors<any>) => void) => {
      const toastError = (message: string) => {
        toast({
          title: message,
          variant: 'destructive',
        })
      }

      console.error(error)

      if (!isAxiosError(error)) {
        toastError(error.message)
        Sentry.captureException(error)
        return
      }

      const { response } = error

      if (response?.status === 422) {
        // Convert snake_case error keys from backend to camelCase for Formik
        const camelCasedErrors = response?.data?.errors
          ? camelCaseObject(response.data.errors)
          : {}

        // Show specific validation error messages if available
        const firstError = Object.values(response?.data?.errors || {})[0]
        if (Array.isArray(firstError) && firstError.length > 0) {
          toastError(firstError[0] as string)
        } else {
          toastError(t('invalid_form_data'))
        }

        console.error('Validation errors:', response.data.errors)
        setErrors?.(camelCasedErrors)
      } else if (response?.status === 403) {
        toastError(t('permission_denied'))
      } else {
        toastError(error.message)
      }
    },
    [t, toast]
  )

  return { handleError }
}
